import { BlockNoteEditor } from '@blocknote/core';
import logoCroppedPng from 'assets/common/logo-cropped.png';
import logoPng from 'assets/common/logo.png';
import { getYoutubeWatchUrl } from 'utils/youtube';
import { generateQrDataUrl } from 'utils/worksheet/generateQrDataUrl';

const escapeHtml = (value: unknown): string =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return ch;
    }
  });

/**
 * Converts a single block to HTML
 */
const convertSingleBlockToHTML = async (
  editor: BlockNoteEditor<any, any, any>,
  block: any
): Promise<string> => {
  switch (block.type) {
    case 'multipleChoice': {
      const { question, options } = block.props;
      const parsedOptions = JSON.parse(options as string) as Array<{
        text: string;
        isCorrect: boolean;
      }>;
      return `
        <div class="multiple-choice-block">
          <p class="mc-question">${escapeHtml(question) || 'Frage hier eingeben...'}</p>
          <p class="mc-hint">Kreuzen Sie die richtige(n) Antwort(en) an.</p>
          <div class="mc-options">
            ${parsedOptions
              .map(
                (opt, idx) => `
              <div class="mc-option">
                <span class="mc-checkbox"></span>
                <span class="mc-option-text">${escapeHtml(opt.text) || `Option ${idx + 1}`}</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      `;
    }
    case 'textQuestion': {
      const { question } = block.props;
      return `
        <div class="text-question-block">
          <p class="tq-question">${question || 'Frage hier eingeben...'}</p>
          <div class="tq-answer-lines">
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
            <div class="tq-answer-line"></div>
          </div>
        </div>
      `;
    }
    case 'selfAssessment': {
      const { question, minScore, maxScore } = block.props;
      const min = typeof minScore === 'number' ? minScore : 0;
      const max = typeof maxScore === 'number' ? maxScore : 5;
      const scores = Array.from({ length: max - min + 1 }, (_, i) => i + min);
      return `
        <div class="self-assessment-block">
          <p class="sa-question">${escapeHtml(question) || 'Frage hier eingeben...'}</p>
          <div class="sa-scale">
            <div class="sa-checkboxes">
              ${scores.map(() => `<span class="sa-checkbox"></span>`).join('')}
            </div>
            <div class="sa-numbers">
              ${scores.map((score) => `<span class="sa-number">${score}</span>`).join('')}
            </div>
            <div class="sa-labels">
              <span class="sa-label">Nicht verstanden</span>
              <span class="sa-label">Vollständig verstanden</span>
            </div>
          </div>
        </div>
      `;
    }
    case 'nhVideo': {
      // TODO(nh-video-deeplink): Prefer QR → Novaheal /app/video/:id (needs videoId on block).
      // Until then, encode a YouTube watch URL. DB Video.qrCodeUrl should match after
      // `npm run regenerate:video-qrcodes -- --force`; print also generates from watchUrl
      // so worksheets without a stored qrCodeUrl still match the Figma layout.
      const { title, description, videoUrl, qrCodeUrl } = block.props;
      const videoUrlString = videoUrl as string;
      const watchUrl = videoUrlString ? getYoutubeWatchUrl(videoUrlString) : '';
      const storedQr = qrCodeUrl as string;

      let qrSrc = storedQr;
      if (watchUrl) {
        try {
          qrSrc = await generateQrDataUrl(watchUrl, 300);
        } catch (error) {
          console.warn('Failed to generate print QR from watch URL, using stored qrCodeUrl', error);
        }
      }

      return `
        <div class="video-block">
          <div class="video-info">
            <p class="video-title">${escapeHtml(title) || 'Novaheal Video'}</p>
            ${description ? `<p class="video-description">${escapeHtml(description)}</p>` : ''}
            ${
              watchUrl
                ? `<div class="video-url">
              <p class="video-url-label">Video-Link:</p>
              <p class="video-url-value">${escapeHtml(watchUrl)}</p>
            </div>`
                : ''
            }
          </div>
          ${
            qrSrc
              ? `<div class="video-qr-column">
            <div class="video-qr-frame">
              <span class="video-qr-corner video-qr-corner-tl"></span>
              <span class="video-qr-corner video-qr-corner-tr"></span>
              <span class="video-qr-corner video-qr-corner-bl"></span>
              <span class="video-qr-corner video-qr-corner-br"></span>
              <img src="${qrSrc}" alt="QR Code für Video" class="video-qr-code" />
            </div>
            <p class="video-qr-label">Video ansehen</p>
          </div>`
              : ''
          }
        </div>
      `;
    }
    default: {
      // For standard blocks, use BlockNote's serializer
      try {
        return await editor.blocksToHTMLLossy([block]);
      } catch (error) {
        console.warn('Failed to convert block to HTML:', block.type, error);
        return '';
      }
    }
  }
};

/**
 * Converts BlockNote blocks (including custom blocks) to HTML for printing
 */
const convertBlocksToHTML = async (
  editor: BlockNoteEditor<any, any, any>,
  blocks: any[]
): Promise<string> => {
  const htmlPromises = blocks.map((block) => convertSingleBlockToHTML(editor, block));
  const htmlParts = await Promise.all(htmlPromises);
  return htmlParts.join('\n');
};

/**
 * Gets the print styles CSS
 */
const getPrintStyles = (worksheetTitle: string, printLogoUrl: string): string => {
  const logoContentUrl = `url(${JSON.stringify(printLogoUrl)})`;
  /* Logo band: 5×1 cm; top @page margin = logo height + gap before content */
  const logoPrintWidth = '5cm';
  const logoPrintHeight = '1cm';
  const logoContentGap = '15mm';
  const pageMarginTop = `calc(${logoPrintHeight} + ${logoContentGap})`;
  const punchMarginLeft = '25mm';
  const pageMarginRight = '15mm';
  const pageMarginBottom = '14mm';

  return `
    * {
      box-sizing: border-box;
    }
    /* Screen preview only — print uses @page margins (body padding reset below). */
    body {
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
      font-size: 16px;
      /* Top: .print-preview-header provides the logo band (≈ pageMarginTop in print). */
      padding: 0 ${pageMarginRight} ${pageMarginBottom} ${punchMarginLeft};
      line-height: 1.4;
      color: #333;
      background: #fff;
    }

    .print-preview-header {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 0 ${logoContentGap} 0;
      padding-bottom: 5mm;
      border-bottom: 1px solid #e8e8e8;
    }

    .print-preview-header img {
      width: ${logoPrintWidth};
      height: ${logoPrintHeight};
      object-fit: contain;
      display: block;
    }

    h1,
    .worksheet-title {
      margin: 0 0 16px 0;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 22px;
      font-weight: 600;
      line-height: 1.2;
      color: #333;
    }
    h2 {
      font-size: 22px;
      margin-top: 20px;
      margin-bottom: 10px;
    }
    h3 {
      font-size: 18px;
      margin-top: 16px;
      margin-bottom: 8px;
    }
    p {
      font-size: 16px;
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .description {
      color: #666;
      margin-bottom: 20px;
      font-style: italic;
    }

    /* Student identification header (Figma: node 17083:7929) */
    .student-header {
      margin: 0 0 24px 0;
      padding: 18px;
      background: #F8F8F8;
    }

    .student-header-title {
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.4;
      color: #333;
      margin: 0 0 20px 0;
    }

    .student-fields {
      display: flex;
      flex-direction: column;
      gap: 28px;
    }

    /* Fixed label column so Name/Datum underlines share one vertical start line */
    .student-field {
      display: grid;
      grid-template-columns: 52px 1fr;
      column-gap: 8px;
      align-items: end;
      min-width: 0;
    }

    .student-field-label {
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 1.4;
      color: #333;
      text-align: right;
      white-space: nowrap;
      margin: 0;
    }

    .student-field-line {
      border-bottom: 1px solid #333;
      height: 20px;
      min-width: 0;
      margin: 0;
    }

    .student-field-split {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 40px;
      align-items: end;
    }

    @media print {
      .student-header {
        background: #F8F8F8;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }

    /* Hide all input fields, textareas, and interactive elements */
    input[type="text"],
    input[type="number"],
    input[type="email"],
    textarea,
    select,
    button,
    [contenteditable="true"],
    [role="textbox"],
    .input,
    .textarea,
    .editable {
      display: none !important;
    }

    /* Hide answer fields and results - but keep answer lines visible */
    [class*="result"],
    [class*="feedback"],
    [class*="score"],
    [data-answer],
    [data-result] {
      display: none !important;
    }

    /* Ensure answer space is visible */
    .answer-space,
    .answer-lines,
    .answer-line {
      display: block !important;
    }

    /* Show question text and labels clearly — do not target span (breaks flex gaps) */
    p, label {
      margin: 8px 0;
    }

    /* Add spacing for question blocks */
    [data-content-type],
    .block {
      margin: 16px 0;
      page-break-inside: avoid;
    }

    /* Style lists properly */
    ul, ol {
      margin: 8px 0;
      padding-left: 24px;
    }

    li {
      margin: 4px 0;
    }

    /* Inline marks (BlockNote / TipTap export: em, strong, u, s, span[data-*]) */
    em, i {
      font-style: italic;
    }
    strong, b {
      font-weight: 700;
    }
    u {
      text-decoration: underline;
    }
    s, del {
      text-decoration: line-through;
    }

    /* Quotes — BlockNote external HTML is a bare <blockquote>.
       Use ::before for the marker: thin border-left often disappears in print. */
    blockquote {
      position: relative;
      margin: 8px 0;
      margin-inline: 0;
      padding-left: 12px;
      border: none;
      color: #7d797a;
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 16px;
    }

    blockquote::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 2px;
      background-color: #808080;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* BlockNote text colors (data attributes, not inline CSS) */
    [data-text-color="gray"] { color: #9b9a97; }
    [data-text-color="brown"] { color: #64473a; }
    [data-text-color="red"] { color: #e03e3e; }
    [data-text-color="orange"] { color: #d9730d; }
    [data-text-color="yellow"] { color: #dfab01; }
    [data-text-color="green"] { color: #4d6461; }
    [data-text-color="blue"] { color: #0b6e99; }
    [data-text-color="purple"] { color: #6940a5; }
    [data-text-color="pink"] { color: #ad1a72; }

    /* BlockNote highlight / background colors */
    [data-background-color="gray"] { background-color: #ebeced; }
    [data-background-color="brown"] { background-color: #e9e5e3; }
    [data-background-color="red"] { background-color: #fbe4e4; }
    [data-background-color="orange"] { background-color: #f6e9d9; }
    [data-background-color="yellow"] { background-color: #fbf3db; }
    [data-background-color="green"] { background-color: #ddedea; }
    [data-background-color="blue"] { background-color: #ddebf1; }
    [data-background-color="purple"] { background-color: #eae4f2; }
    [data-background-color="pink"] { background-color: #f4dfeb; }

    [data-text-color]:not([data-text-color="default"]),
    [data-background-color]:not([data-background-color="default"]) {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* Custom block styling */
    .custom-block {
      margin: 40px 0;
      padding: 16px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: #f9f9f9;
      page-break-inside: avoid;
    }

    .custom-block h3 {
      margin: 0 0 12px 0;
      font-size: 16px;
      color: #333;
      font-weight: 600;
    }

    .custom-block p {
      margin: 8px 0;
    }

    .custom-block ul {
      list-style: none;
      padding-left: 0;
    }

    .custom-block li {
      margin: 6px 0;
      padding-left: 24px;
    }

    .custom-block li span {
      margin-right: 8px;
    }

    /* Text question block (Figma: node 17089:8035) */
    .text-question-block {
      margin: 40px 0;
      page-break-inside: avoid;
      border: none;
      border-radius: 0;
      background: transparent;
    }

    .tq-question {
      margin: 0 0 24px 0;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.4;
      color: #333;
    }

    .tq-answer-lines {
      display: flex;
      flex-direction: column;
      gap: 30px;
      width: 100%;
    }

    .tq-answer-line {
      width: 100%;
      height: 0;
      border-bottom: 1px solid #333;
    }

    /* Multiple-choice block (Figma: node 17086:7995) */
    .multiple-choice-block {
      margin: 40px 0;
      page-break-inside: avoid;
    }

    .mc-question {
      margin: 0 0 4px 0;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.4;
      color: #333;
    }

    .mc-options {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .mc-option {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .mc-checkbox {
      flex-shrink: 0;
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 1px solid #ccc;
      border-radius: 1px;
      margin: 0;
    }

    .mc-option-text {
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 16px;
      font-style: normal;
      line-height: 1.4;
      color: #333;
      margin: 0;
    }

    .mc-hint {
      margin: 0 0 12px 0;
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      font-style: normal;
      color: #999;
    }

    /* Self-assessment block (Figma: node 17089:8058) */
    .self-assessment-block {
      margin: 40px 0;
      page-break-inside: avoid;
      border: none;
      border-radius: 0;
      background: transparent;
    }

    .sa-question {
      margin: 0 0 16px 0;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.4;
      color: #333;
    }

    .sa-scale {
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-width: 515px;
    }

    .sa-checkboxes,
    .sa-numbers {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    .sa-checkbox {
      flex-shrink: 0;
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 1px solid #ccc;
      border-radius: 1px;
      box-sizing: border-box;
    }

    .sa-number {
      flex-shrink: 0;
      width: 16px;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 500;
      font-size: 12px;
      line-height: 1.3;
      color: #333;
      text-align: center;
    }

    .sa-labels {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      margin-top: 2px;
    }

    .sa-label {
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 400;
      font-size: 12px;
      line-height: 1.3;
      color: #666;
    }

    /* Video block (Figma: node 17083:7941) */
    .video-block {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 40px;
      margin: 40px 0;
      width: 100%;
      page-break-inside: avoid;
      border: none;
      background: transparent;
    }

    .video-info {
      flex: 1;
      min-width: 0;
    }

    .video-title {
      margin: 0 0 8px 0;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.3;
      color: #333;
    }

    .video-description {
      margin: 0 0 16px 0;
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      line-height: 1.4;
      color: #4c4c4c;
    }

    .video-url {
      margin: 0;
    }

    .video-url-label {
      margin: 0;
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      line-height: 1.4;
      color: #666;
    }

    .video-url-value {
      margin: 0;
      font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.4;
      color: #333;
      word-break: break-all;
    }

    .video-qr-column {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 107px;
      margin-left: auto;
    }

    .video-qr-frame {
      position: relative;
      width: 107px;
      height: 106px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
    }

    .video-qr-corner {
      position: absolute;
      width: 16px;
      height: 16px;
      border-color: #ccc;
      border-style: solid;
      pointer-events: none;
      margin: 0;
    }

    .video-qr-corner-tl {
      top: 0;
      left: 0;
      border-width: 2px 0 0 2px;
    }

    .video-qr-corner-tr {
      top: 0;
      right: 0;
      border-width: 2px 2px 0 0;
    }

    .video-qr-corner-bl {
      bottom: 0;
      left: 0;
      border-width: 0 0 2px 2px;
    }

    .video-qr-corner-br {
      bottom: 0;
      right: 0;
      border-width: 0 2px 2px 0;
    }

    .video-qr-code {
      width: 90px;
      height: 90px;
      display: block;
      background: white;
    }

    .video-qr-label {
      margin: 4px 0 0 0;
      font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 400;
      font-size: 10px;
      line-height: 1.3;
      color: #666;
      text-align: center;
      white-space: nowrap;
    }

    @media print {
      @page {
        margin: ${pageMarginTop} ${pageMarginRight} ${pageMarginBottom} ${punchMarginLeft};

        @top-center {
          content: ${logoContentUrl};
          width: ${logoPrintWidth};
          height: 15mm;
          vertical-align: top;
        }

        @bottom-center {
          content: ${JSON.stringify(worksheetTitle)} " - Seite " counter(page) " von " counter(pages);
          font-size: 8pt;
          color: #666;
        }
      }

      .print-preview-header {
        display: none;
      }

      body {
        padding: 0;
        margin: 0;
      }

      /* Ensure page breaks work well */
      h1, h2, h3 {
        page-break-after: avoid;
      }

      [data-content-type],
      .block,
      .custom-block,
      .video-block {
        page-break-inside: avoid;
      }

      /* Remove background colors for printing */
      .custom-block {
        background: white;
        border: 1px solid #333;
      }

      .text-question-block,
      .self-assessment-block,
      .multiple-choice-block,
      .video-block {
        border: none;
        background: white;
      }
    }
  `;
};

/**
 * Opens a print dialog with the worksheet content
 */
export const printWorksheet = async (params: {
  editor: BlockNoteEditor<any, any, any>;
  worksheetTitle: string;
}): Promise<{ success: boolean; error?: string }> => {
  try {
    const { editor, worksheetTitle } = params;

    // Convert all blocks (including custom ones) to HTML
    const htmlContent = await convertBlocksToHTML(editor, editor.document);

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      return {
        success: false,
        error: 'Popup wurde blockiert. Bitte erlaube Popups für diese Seite.',
      };
    }

    // Write HTML content with styles
    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${escapeHtml(worksheetTitle)}</title>
            <meta charset="UTF-8">
            <style>
              ${getPrintStyles(worksheetTitle, logoCroppedPng)}
            </style>
          </head>
          <body>
            <header class="print-preview-header" aria-hidden="true">
              <img src="${logoPng}" alt="" />
            </header>
            <h1 class="worksheet-title">${escapeHtml(worksheetTitle)}</h1>
            <div class="student-header">
              <div class="student-header-title">Persönliche Angaben</div>
              <div class="student-fields">
                <div class="student-field">
                  <span class="student-field-label">Name:</span>
                  <span class="student-field-line"></span>
                </div>
                <div class="student-field-split">
                  <div class="student-field">
                    <span class="student-field-label">Datum:</span>
                    <span class="student-field-line"></span>
                  </div>
                  <div class="student-field">
                    <span class="student-field-label">Kurs:</span>
                    <span class="student-field-line"></span>
                  </div>
                </div>
              </div>
            </div>
            <div class="content">
              ${htmlContent}
            </div>
          </body>
        </html>
      `);

    printWindow.document.close();

    // Trigger print dialog after content and images load
    printWindow.onload = () => {
      // Wait a bit for images to load
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    };

    return { success: true };
  } catch (error) {
    console.error('Print error:', error);
    return {
      success: false,
      error: 'Ein Fehler ist beim Drucken aufgetreten.',
    };
  }
};
